import React from 'react';
import { useEffect } from 'react';
import './AnimatedPage.scss';

const AnimatedPage = ({ children }) => {
  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '50px'
    });

    const pageElement = document.querySelector('.animated-page');
    if (pageElement) {
      // Add animations to main sections
      const sections = pageElement.querySelectorAll('div[class*="even-grid"], div[class*="text-paragraph"], div[class*="column-grid"], div[class*="next"]');
      sections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });
      
      // Add animations to individual features
      const features = pageElement.querySelectorAll('div[class*="features-grid-odd"], div[class*="features-grid-even"]');
      features.forEach(feature => {
        feature.setAttribute('data-animate', 'true');
        feature.classList.add('fade-in');
      });

      // Add animations to intro sections that contain both heading and content
      // Target the div containing the heading and the paragraph div together
      const introSections = pageElement.querySelectorAll('.intro > div:not(:first-child)');
      introSections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to the heading container and paragraph container as a group
      const headingContainers = pageElement.querySelectorAll('.intro div[id="heading"]');
      headingContainers.forEach(heading => {
        const parentDiv = heading.closest('.intro > div');
        if (parentDiv) {
          parentDiv.setAttribute('data-animate', 'true');
          parentDiv.classList.add('fade-in');
        }
      });

      // Add animations to about page structure
      const aboutSections = pageElement.querySelectorAll('.about > div:not(.image-mask-rect)');
      aboutSections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to section containers that have titles followed by content
      // This targets sections like "challenge", "actions", "action 1", etc.
      const sectionContainers = pageElement.querySelectorAll('div[id="challenge"], div[id="actions"], div[id^="action"], div[id="result"]');
      sectionContainers.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to sections with h3/b titles followed by even-grid or text-paragraph
      const titleContentSections = pageElement.querySelectorAll('div:has(h3) + div[class*="even-grid"], div:has(b) + div[class*="even-grid"], div:has(h3) + div[class*="text-paragraph"], div:has(b) + div[class*="text-paragraph"]');
      titleContentSections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Additional targeting for action sections to ensure title and content animate together
      // This targets the specific structure of action sections with b elements
      const actionSections = pageElement.querySelectorAll('div[id^="action"]');
      actionSections.forEach(actionSection => {
        // Find the title div (contains b element) and content div (even-grid) within this action section
        const titleDiv = actionSection.querySelector('div:has(b)');
        const contentDiv = actionSection.querySelector('div[class*="even-grid"]');
        
        if (titleDiv && contentDiv) {
          // Mark both the title and content divs for animation
          titleDiv.setAttribute('data-animate', 'true');
          titleDiv.classList.add('fade-in');
          contentDiv.setAttribute('data-animate', 'true');
          contentDiv.classList.add('fade-in');
        }
      });

      // Add animations to Samana page sections (and similar pages) with h3 titles
      // This targets sections like "background", "planning", "problem space", etc.
      const samanaSections = pageElement.querySelectorAll('div[id="background"], div[id="planning"], div[id="problem space"], div[id="Goals"], div[id="phases"], div[id="design process"], div[id="visual design"], div[id="content editing portal"], div[id="focus groups"], div[id="launch"], div[id="analytics"], div[id="summary"]');
      samanaSections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to Go Studio FX page sections
      const goStudioFXSections = pageElement.querySelectorAll('div[id="constraints and limitations"], div[id="target audience"], div[id="early concept"]');
      goStudioFXSections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to GoXLR page sections
      const goXLRSections = pageElement.querySelectorAll('div[id="problem"], div[id="actions"], div[id="result"]');
      goXLRSections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to Morgan Stanley page sections
      const morganStanleySections = pageElement.querySelectorAll('div[id^="feature"]');
      morganStanleySections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to Teamwrk page sections
      const teamwrkSections = pageElement.querySelectorAll('div[id="design guidelines"], div[id="user interface"], div[id="responsive ui"], div[id="email templates"]');
      teamwrkSections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to Blender page sections
      const blenderSections = pageElement.querySelectorAll('div[id="learning from others"], div[id="colour and typography"], div[id="mixer screen"], div[id="bluetooth"]');
      blenderSections.forEach(section => {
        section.setAttribute('data-animate', 'true');
        section.classList.add('fade-in');
      });

      // Add animations to sections with h3 titles that are followed by even-grid content
      // This ensures the title and content animate together as one block
      const h3Sections = pageElement.querySelectorAll('div:has(h3)');
      h3Sections.forEach(h3Section => {
        // Check if this section has an h3 followed by even-grid content
        const h3 = h3Section.querySelector('h3');
        const nextSibling = h3Section.nextElementSibling;
        
        if (h3 && nextSibling && (nextSibling.classList.contains('even-grid') || nextSibling.classList.contains('text-paragraph'))) {
          // Mark the entire section for animation
          h3Section.setAttribute('data-animate', 'true');
          h3Section.classList.add('fade-in');
        }
      });

      // Add animations to sections with b titles that are followed by even-grid content
      // This ensures the title and content animate together as one block
      const bSections = pageElement.querySelectorAll('div:has(b)');
      bSections.forEach(bSection => {
        // Check if this section has a b element followed by even-grid content
        const b = bSection.querySelector('b');
        const nextSibling = bSection.nextElementSibling;
        
        if (b && nextSibling && (nextSibling.classList.contains('even-grid') || nextSibling.classList.contains('text-paragraph'))) {
          // Mark the entire section for animation
          bSection.setAttribute('data-animate', 'true');
          bSection.classList.add('fade-in');
        }
      });

      // Add animations to sections that have h3 titles within them
      // This targets sections where the h3 is inside the section div
      const sectionsWithH3 = pageElement.querySelectorAll('div:has(h3)');
      sectionsWithH3.forEach(section => {
        const h3 = section.querySelector('h3');
        const evenGrid = section.querySelector('div[class*="even-grid"]');
        
        if (h3 && evenGrid) {
          // Mark the entire section for animation
          section.setAttribute('data-animate', 'true');
          section.classList.add('fade-in');
        }
      });
    }

    const animatedElements = document.querySelectorAll('[data-animate]');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="animated-page">
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            'data-animate': true,
            className: `fade-in ${child.props.className || ''}`.trim()
          });
        }
        return child;
      })}
    </div>
  );
};

export default AnimatedPage;
